import { OptionType } from 'components';

export type TypeMode = 'create' | 'update' | 'newUser' | 'profil';

const stepsByMode: { [key in TypeMode]: string[] } = {
    create: ['Information', 'Périmètre', 'Droits', 'Code gestionnaire', 'Validation'],
    update: ['Information', 'Périmètre', 'Droits', 'Code gestionnaire'],
    newUser: ['Information', 'Code gestionnaire', 'Validation'],
    profil: ['Information', 'Périmètre', 'Droits', 'Code gestionnaire'],
};

export const formStepListByMode = (mode: TypeMode): string[] => {
    return stepsByMode[mode];
};

export const AlertFrequency: OptionType<string>[] = [
    {
        label: 'Quotidienne',
        value: '1',
    },
    {
        label: 'Hebdomadaire',
        value: '2',
    },
    {
        label: 'Mensuelle',
        value: '3',
    },
];

export enum TypeTitre {
    MR = 'M',
    MME = 'MME',
    MLLE = 'MLLE',
}

export enum TypeRole {
    ADMIN = 'ADMIN',
    RESPONSABLE = 'RESPONSABLE',
    UTILISATEUR = 'UTILISATEUR',
}

export enum TypesRolesCotisation {
    ROLE_CONTRIBUTION_SEE = 'role_contribution_see',
    ROLE_CONTRIBUTION_DECLARE = 'role_contribution_declare',
    ROLE_CONTRIBUTION_SEND = 'role_contribution_send',
}

export enum Roles {
    // User
    ROLE_ADD_USER = 'ROLE_ADD_USER',
    ROLE_EDIT_USER = 'ROLE_EDIT_USER',
    ROLE_AFFILIATE_NEW_SALARY = 'ROLE_AFFILIATE_NEW_SALARY',
    ROLE_SEE_BIA = 'ROLE_SEE_BIA',
    ROLE_VALID_ASSURED = 'ROLE_VALID_ASSURED',
    ROLE_CONTRIBUTION_SEE = 'ROLE_CONTRIBUTION_SEE',
    ROLE_HISTORY_ASSURED = 'ROLE_HISTORY_ASSURED',
    ROLE_SUBSCRIPTION_EPAYMENT = 'ROLE_SUBSCRIPTION_EPAYMENT',
    ROLE_CONTACT = 'ROLE_CONTACT',
    ROLE_ADD_WORK_STOPPING = 'ROLE_ADD_WORK_STOPPING',
    ROLE_DOCUMENTATION = 'ROLE_DOCUMENTATION',
    ROLE_RADIATION = 'ROLE_RADIATION',
    ROLE_RT_STATISTIC = 'ROLE_RT_STATISTIC',
    ROLE_STATISTIC = 'ROLE_STATISTIC',
    ROLE_SEE_GARANTIE = 'ROLE_SEE_GARANTIE',
    ROLE_DOSSIER_PREV = 'ROLE_DOSSIER_PREV',
    ROLE_DOCUMENTS_LIDL = 'ROLE_DOCUMENTS_LIDL',
}

export const droitsMapping = {
    ROLE_ADD_USER: 'role_add_user',
    ROLE_EDIT_USER: 'role_edit_user',
    ROLE_AFFILIATE_NEW_SALARY: 'role_affiliate_new_salary',
    ROLE_SEE_BIA: 'role_see_bia',
    ROLE_HISTORY_ASSURED: 'role_history_assured',
    ROLE_VALID_ASSURED: 'role_valid_assured',
    ROLE_CONTRIBUTION_SEE: 'role_contribution_see',
    ROLE_CONTRIBUTION_DECLARE: 'role_contribution_declare',
    ROLE_CONTRIBUTION_SEND: 'role_contribution_send',
    ROLE_SUBSCRIPTION_EPAYMENT: 'role_subscription_epayment',
    ROLE_CONTACT: 'role_contact',
    ROLE_ADD_WORK_STOPPING: 'role_add_work_stopping',
    ROLE_DOCUMENTATION: 'role_documentation',
    ROLE_RADIATION: 'role_radiation',
    ROLE_RT_STATISTIC: 'role_rt_statistic',
    ROLE_STATISTIC: 'role_statistic',
    ROLE_SEE_GARANTIE: 'role_see_garantie',
    ROLE_ADMIN_DOCUMENT: 'role_admin_document',
    ROLE_DOCUMENTS_LIDL: 'role_documents_lidl',
};

export const formKeys = [
    'id',
    'accesWeb',
    'codeGestionnaire',
    'codeGestionnaireMultiple',
    'confirmation',
    'email',
    'etat',
    'fonction',
    'login',
    'nom',
    'password',
    'prenom',
    'responsable',
    'role',
    'telephone',
    'titre',
];

export interface Utilisateur {
    id: number;
    responsable: Responsable;
    fonction: string;
    titre: TypeTitre;
    nom: string;
    prenom: string;
    telephone: string;
    login: string;
    role: keyof typeof TypeRole;
    codeGestionnaire: string;
    email: string;
    accesWeb: boolean;
    codeGestionnaireMultiple: CodeGestionnaire[];
    roles: string[];
    perimetre: {
        risques: string[];
        souscripteurs: string[];
        colleges: string[];
        excludeVIP: boolean,
    };
    password?: string;
    alertBia?: {
        id: number;
        actif: boolean;
        frequence: string;
        lastNotification: string;
    };
}

export const initUtilisateur = (): UserFormData => {
    const Utilisateur = {
        id: 0,
        responsable: null,
        fonction: '',
        titre: TypeTitre.MR,
        nom: '',
        prenom: '',
        telephone: '',
        login: '',
        role: TypeRole.UTILISATEUR,
        codeGestionnaire: '',
        email: '',
        accesWeb: 'true',
        codeGestionnaireMultiple: [],
        roles: [],
        perimetre: {
            risques: { sante: false, prevoyance: false },
            souscripteurs: [],
            colleges: [],
            excludeVIP: false,
        },
        alertBia: {
            id: null,
            actif: false,
            frequence: '1',
            lastNotification: null,
        },
    };

    return Utilisateur;
};

export const initUtilisateurExample = () => {
    const Utilisateur = {
        id: 0,
        responsable: null,
        fonction: 'Test',
        titre: TypeTitre.MR,
        nom: 'HELVAS',
        prenom: 'BORIS',
        telephone: '0000000000',
        login: 'boris@gmail.com',
        role: TypeRole.UTILISATEUR,
        codeGestionnaire: '',
        email: 'boris@gmail.com',
        accesWeb: 'true',
        codeGestionnaireMultiple: [],
        roles: [],
        perimetre: {
            risques: { sante: false, prevoyance: false },
            souscripteurs: [],
            colleges: [],
            excludeVIP: false
        },
        alertBia: {
            id: null,
            actif: false,
            frequence: '1',
            lastNotification: null,
        },
        password: 'Ageo*20242024',
        confirmation: 'Ageo*20242024',
    };
    return Utilisateur;
};

export type CodeGestionnaire = {
    id: string;
    idSouscrip: string;
    codeGestionnaire: string;
};

export type Responsable = {
    id: number;
    prenom: string;
    nom: string;
};

export type UserFormData = {
    id: number;
    nom: string;
    prenom: string;
    titre: TypeTitre;
    telephone: string;
    email: string;
    fonction: string;
    role: string;
    responsable?: Responsable;
    accesWeb: string;
    login: string;
    codeGestionnaire: string;
    codeGestionnaireMultiple: CodeGestionnaire[];
    perimetre: {
        risques: { sante: boolean; prevoyance: boolean };
        souscripteurs: OptionType[];
        colleges: OptionType[];
        excludeVIP: boolean,
    };
    roles: string[];
    password?: string;
    confirmation?: string;
    alertBia: {
        id: number;
        actif: boolean;
        frequence: string;
        lastNotification: string;
    };
};

export type Perimetre = {
    typ: string;
    codcol: string;
    college: string;
    codSouscrip: string;
    souscrip: string;
};

export const titres: OptionType<string>[] = [
    {
        label: 'utilisateur.titre.mr',
        value: TypeTitre.MR,
    },
    {
        label: 'utilisateur.titre.mme',
        value: TypeTitre.MME,
    },
    // {
    //     label: 'utilisateur.titre.mlle',
    //     value: TypeTitre.MLLE,
    // },
];

export const roles: OptionType<string>[] = [
    {
        label: 'utilisateur.roles.admin',
        value: TypeRole.ADMIN,
    },
    {
        label: 'utilisateur.roles.responsable',
        value: TypeRole.RESPONSABLE,
    },
    {
        label: 'utilisateur.roles.utilisateur',
        value: TypeRole.UTILISATEUR,
    },
];

export const rolesCotisation: OptionType<string>[] = [
    {
        label: 'utilisateur.droits.cotisationVoir',
        value: TypesRolesCotisation.ROLE_CONTRIBUTION_SEE,
    },
    {
        label: 'utilisateur.droits.cotisationDeclarer',
        value: TypesRolesCotisation.ROLE_CONTRIBUTION_DECLARE,
    },
    {
        label: 'utilisateur.droits.cotisationEnvoyer',
        value: TypesRolesCotisation.ROLE_CONTRIBUTION_SEND,
    },
];

export const accesWebOptions: OptionType<string>[] = [
    {
        label: 'OUI',
        value: 'true',
    },
    {
        label: 'NON',
        value: 'false',
    },
];
